import React from 'react';
import { X } from 'lucide-react';
import { tone, fmtQty, fmtDate } from './ageingUtils';

const COLS = {
  greige: [['code', 'Bundle', 'font-mono text-xs'], ['design_name', 'Design'], ['sort_no', 'Sort No'], ['colour', 'Colour'], ['qty', 'Qty'], ['location', 'Location']],
  sfg: [['code', 'Bundle', 'font-mono text-xs'], ['sku_code', 'SKU', 'font-mono text-xs'], ['design_name', 'Design'], ['shade', 'Shade'], ['size', 'Size'], ['qty', 'Pieces'], ['location', 'Bin']],
  fg: [['code', 'USN', 'font-mono text-xs'], ['sku_code', 'SKU', 'font-mono text-xs'], ['design_name', 'Design'], ['shade', 'Shade'], ['size', 'Size'], ['location', 'Bin']],
};

const drillText = (drill, buckets) => {
  const parts = [];
  if (drill?.group) {
    const g = drill.group;
    parts.push([g.design_name || '(no design)', g.shade, g.size, g.sku_code].filter(Boolean).join(' · '));
  }
  if (drill?.bucket !== null && drill?.bucket !== undefined) {
    parts.push(drill.bucket < 0 ? 'No put-away date' : `${buckets[drill.bucket]?.label} days`);
  }
  return parts.join('  —  ');
};

export const AgeingItemsTable = ({ storage, data, loading, drill, onClearDrill }) => {
  const cols = COLS[storage];
  const n = data?.buckets?.length || 1;
  const label = data ? drillText(drill, data.buckets) : '';

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="ageing-items">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-stone-200">
        {label ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-stone-900 text-white text-sm pl-3 pr-1 py-1" data-testid="ageing-drill-chip">
            {label}
            <button type="button" onClick={onClearDrill} className="rounded-full p-0.5 hover:bg-white/20" data-testid="ageing-drill-clear-btn"><X className="w-3.5 h-3.5" /></button>
          </span>
        ) : <span className="text-sm text-stone-600">All items, oldest first</span>}
        {data && (
          <span className="text-sm text-stone-500 tabular-nums" data-testid="ageing-items-count">
            {data.truncated ? `Showing first ${data.rows.length.toLocaleString('en-IN')} of ${data.total.toLocaleString('en-IN')} — download Excel for all` : `${data.total.toLocaleString('en-IN')} ${data.total === 1 ? 'item' : 'items'}`}
          </span>
        )}
      </div>
      <div className="overflow-auto max-h-[65vh]">
        {loading ? <p className="py-16 text-center text-stone-500">Loading items…</p> : (
          <table className="w-full text-sm" data-testid="ageing-items-table">
            <thead className="sticky top-0 bg-stone-900 text-white">
              <tr>
                {cols.map(([k, l]) => <th key={k} className={`px-3 py-2 font-semibold whitespace-nowrap ${k === 'qty' ? 'text-right' : 'text-left'}`}>{l}</th>)}
                <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">First put-away</th>
                <th className="px-3 py-2 text-right font-semibold">Age</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={`${r.code}-${i}`} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`ageing-item-${r.code}`}>
                  {cols.map(([k, , cls]) => (
                    <td key={k} className={`px-3 py-1.5 whitespace-nowrap ${cls || ''} ${k === 'qty' ? 'text-right tabular-nums font-semibold' : ''}`}>
                      {k === 'qty' ? `${fmtQty(r.qty)}${storage === 'greige' && r.unit === 'Mtr' ? ' Mtr' : ''}` : (r[k] || '—')}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 whitespace-nowrap text-stone-600">{fmtDate(r.first_put_away)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold tabular-nums ${tone(r.bucket, n).cell}`} data-testid={`ageing-item-age-${r.code}`}>
                      {r.age_days === null || r.age_days === undefined ? 'no date' : `${r.age_days}d`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && data && data.rows.length === 0 && <p className="py-16 text-center text-stone-500" data-testid="ageing-items-empty">No items</p>}
      </div>
    </div>
  );
};
